import {
  Chip,
  Container,
  Divider,
  Grid,
  makeStyles,
  Paper,
  Typography
} from '@material-ui/core';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext, UserDispatchContext } from '../../Contexts/UserContext';
import { ConstructorContractsContext } from '../../Contexts/ContractsContext';
import { history } from '../../Routes/history';
import { get as _get } from 'lodash';
import { getConstructorContractsAPI } from '../../API/Constructors/ConstructorsAPI';
import { AlertsDispatchContext } from '../../Contexts/AlertsContext';
import { ALERT_TYPE } from '../../Components/Alerts/alert_enums';
import { logoutUser } from '../../Utils/User/Actions';
import { redirectOnAuthFailure } from '../../Utils/Errors/Errors';
import { fullName } from '../../Utils/Format/Names';
import ProjectAccordion from '../../Components/Accordion/ProjectAccordion';
import BaseFilter from '../../Components/Filters/BaseFilter';
import ComboBoxInput from '../../Components/Inputs/ComboBoxInput';
import ErrorState from '../../Components/EmptyState/ErrorState';
import Skeleton from '@material-ui/lab/Skeleton';
import BaseButton from '../../Components/Buttons/BaseButton';
import { Company } from '../../Configs/general';
import { formatArraytoSampledText } from '../../Utils/Misc/Array';

const getMissingProjectsMessage = missingProjects => {
  if (missingProjects.length === 1) {
    return `No se pudo encontrar la etapa asociada a ${Company.contractConjugation.regular.singular.article}: ${missingProjects[0]}`;
  }

  const baseMessage = `No se pudo encontrar las etapas asociadas a ${Company.contractConjugation.regular.plural.article}:`;

  return `${baseMessage} ${formatArraytoSampledText(missingProjects, 3)}`;
};

const parseProjectsData = (contracts, projects) => {
  const contractIdsSet = contracts.map(contract => contract.id);

  return projects.map(project => {
    return {
      ...project,
      ...project.phases.reduce(
        (acc, phase) => ({
          totalValue: acc.totalValue + phase.quotationData.totalValue,
          totalUnits: acc.totalUnits + phase.units,
          totalActivatedUnits: acc.totalActivatedUnits + phase.activeContracts
        }),
        { totalValue: 0, totalUnits: 0, totalActivatedUnits: 0 }
      ),
      phases: project.phases.map(({ quotationData, ...phase }) => ({
        ...phase,
        totalValue: quotationData.totalValue,
        disabled: !contractIdsSet.includes(phase.contractId)
      }))
    };
  });
};

const getProjectList = projects =>
  projects.map(({ name }, index) => ({ index, name }));

const EmptyState = () => (
  <Grid item xs={12}>
    <Paper variant="outlined">
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid item xs={9} md={3}>
          <Skeleton variant="text" />
          <Skeleton variant="text" />
        </Grid>
        <Grid item xs={7} md={2}>
          <Skeleton variant="text" />
        </Grid>
        <Grid item xs={7} md={2}>
          <Skeleton variant="text" />
        </Grid>
        <Grid item xs={12} md="auto">
          <Skeleton variant="text">
            <BaseButton disabled>Solicitar cotización</BaseButton>
          </Skeleton>
        </Grid>
      </Grid>
    </Paper>
  </Grid>
);

const ConstructorsGDC = () => {
  const classes = useStyles();
  const constructorContracts = useContext(ConstructorContractsContext);
  const setAlert = useContext(AlertsDispatchContext);
  const setCurrentUser = useContext(UserDispatchContext);
  const user = useContext(UserContext);
  const phases = useRef([]);
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [displayProjects, setDisplayProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [displayPhases, setDisplayPhases] = useState([]);
  const [selectedPhases, setSelectedPhases] = useState([]);

  const isFiltered = useMemo(
    () =>
      (selectedProjects && selectedProjects.length > 0) ||
      (selectedPhases && selectedPhases.length > 0),
    [selectedProjects, selectedPhases]
  );

  const disableFields = useMemo(() => projects.length === 0, [projects]);

  const authToken = _get(user, 'token');

  useEffect(() => {
    if (constructorContracts && constructorContracts.length === 0) {
      history.push('/');
      return;
    }

    let ignoreRequest = false;

    const fetchContracts = async () => {
      if (!user) {
        history.push('/');
        return;
      }

      if (ignoreRequest) {
        return;
      }

      setIsLoading(true);

      const response = await getConstructorContractsAPI(authToken);

      if (!response.success) {
        setIsLoading(false);
        if (
          redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
        ) {
          return;
        }

        setAlert({
          type: ALERT_TYPE.ERROR,
          message: 'Ocurrió un error, intente más tarde'
        });
        return;
      }

      const projectsData = parseProjectsData(
        constructorContracts,
        response.data.projects
      );

      const allPhases = projectsData.flatMap((project, projectIndex) =>
        // eslint-disable-next-line max-nested-callbacks
        project.phases.map((phase, index) => ({
          projectName: project.name,
          name: `${phase.contractId} - ${phase.name}`,
          projectIndex,
          index
        }))
      );

      const missingProjects = response.data.missingProjects;

      if (missingProjects.length > 0) {
        const message = getMissingProjectsMessage(missingProjects);
        setAlert({
          type: ALERT_TYPE.WARNING,
          message
        });
      }

      phases.current = allPhases;
      setProjects(projectsData);
      setDisplayProjects(getProjectList(projectsData));
      setDisplayPhases(phases.current);
      setIsLoading(false);
    };

    fetchContracts();
    return () => {
      ignoreRequest = true;
    };
  }, [
    constructorContracts,
    user,
    authToken,
    setAlert,
    setProjects,
    setCurrentUser
  ]);

  const renderFilters = () => (
    <>
      <Grid item xs={12} md={4}>
        <ComboBoxInput
          freeSolo={false}
          multiple
          noOptionsText="Sin proyectos"
          disabled={disableFields}
          TextInputProps={{
            label: 'Proyectos'
          }}
          getOptionLabel={project => String(project.name)}
          options={displayProjects}
          value={selectedProjects}
          name="projects"
          onChange={(_, projectList) => {
            setSelectedProjects(projectList);
            setSelectedPhases(
              selectedPhases.filter(phase =>
                projectList.find(
                  project => project.index === phase.projectIndex
                )
              )
            );

            if (projectList.length === 0) {
              setDisplayPhases(phases.current);
              return;
            }

            setDisplayPhases([
              ...phases.current.filter(phase =>
                projectList.find(item => item.index === phase.projectIndex)
              )
            ]);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ComboBoxInput
          freeSolo={false}
          multiple
          noOptionsText="Sin etapas"
          disabled={disableFields}
          TextInputProps={{
            label: 'Etapas'
          }}
          groupBy={phase => phase.projectName}
          getOptionLabel={phase => String(phase.name)}
          options={displayPhases}
          value={selectedPhases}
          name="phases"
          onChange={(_, phaseList) => {
            setSelectedPhases(phaseList);
            const missingProjects = [];
            for (let i = 0; i < phaseList.length; i += 1) {
              const { projectIndex } = phaseList[i];
              if (
                !selectedProjects.find(
                  project => project.index === projectIndex
                )
              ) {
                missingProjects.push(displayProjects[projectIndex]);
              }
            }

            setSelectedProjects([...selectedProjects, ...missingProjects]);
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={`${option.name}-${index}`}
                variant="outlined"
                label={option.name}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </Grid>
    </>
  );

  const renderProjects = () => {
    if (isLoading) {
      return (
        <>
          <EmptyState />
          <EmptyState />
          <EmptyState />
        </>
      );
    }

    if (projects.length === 0) {
      return <ErrorState messageTitle="No se han encontrado proyectos" />;
    }

    let projectList = displayProjects;

    if (selectedProjects.length > 0) {
      projectList = selectedProjects.sort((a, b) => a.index - b.index);
    }

    return projectList.map(({ index }) => {
      const { phases: projectPhases, ...project } = projects[index];
      let phaseList = projectPhases;

      if (selectedPhases.length > 0) {
        phaseList = selectedPhases
          .filter(selectedPhase => selectedPhase.projectIndex === index)
          .sort((a, b) => a.index - b.index)
          .map(({ index: phaseIndex }) => projectPhases[phaseIndex]);
      }

      return (
        <Grid key={project.id} item xs={12}>
          <ProjectAccordion project={project} phases={phaseList} />
        </Grid>
      );
    });
  };
  return (
    <Container maxWidth="md" className={classes.root}>
      <Typography className={classes.title}>
        Bienvenido,{' '}
        <strong>{fullName(user, 'firstName', 'lastName', true)}</strong>
      </Typography>
      <Grid
        container
        spacing={1}
        className={classes.filterContainer}
        alignItems="center"
      >
        <Grid item xs={12}>
          <BaseFilter
            filtersLabel="Filtros:"
            filters={renderFilters}
            clearFilters={() => {
              setSelectedProjects([]);
              setSelectedPhases([]);
              setDisplayPhases(phases.current);
            }}
            showClearFilters={isFiltered}
            filterModalTitle="Volver"
            disableClearOutside={false}
            filterButtonOn={isFiltered}
          />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {renderProjects()}
      </Grid>
    </Container>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  title: {
    fontSize: '1em',
    textTransform: 'capitalize',
    marginBottom: theme.spacing(2)
  },
  filterContainer: {
    marginBottom: theme.spacing(4)
  }
}));
export default ConstructorsGDC;
